/**
 * DbSelectionButton - 选择按钮组件
 *
 * 特性：
 * - 支持单选和多选模式
 * - 支持受控和非受控模式
 * - 选中时呈现边框 + 背景色视觉效果
 * - 支持禁用状态（带删除线）
 * - 支持自定义颜色、圆角、间距等样式
 * - 可用于 SKU 属性选择、筛选标签等场景
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import type { DbSelectionButtonProps, SelectionOption } from './DbSelectionButton.types';
import { createStyles, DEFAULTS } from './DbSelectionButton.styles';

function DbSelectionButtonInner<T = string>({
    mode = 'single',
    options,
    value,
    defaultValue,
    onChange,
    max,
    min = 0,
    deselectable = false,
    disabled = false,
    // 样式配置
    activeColor = DEFAULTS.activeColor,
    activeTextColor = DEFAULTS.activeTextColor,
    activeBackgroundColor = DEFAULTS.activeBackgroundColor,
    borderColor = DEFAULTS.borderColor,
    backgroundColor = DEFAULTS.backgroundColor,
    textColor = DEFAULTS.textColor,
    disabledTextColor = DEFAULTS.disabledTextColor,
    disabledBackgroundColor = DEFAULTS.disabledBackgroundColor,
    borderRadius = DEFAULTS.borderRadius,
    borderWidth = DEFAULTS.borderWidth,
    fontSize = DEFAULTS.fontSize,
    itemPadding,
    gap = DEFAULTS.gap,
    columns,
    // 容器样式
    style,
    itemStyle,
    textStyle,
    activeItemStyle,
    activeTextStyle,
    disabledItemStyle,
    disabledTextStyle,
}: DbSelectionButtonProps<T>) {
    const styles = useMemo(() => createStyles(), []);

    // 内部状态（非受控模式）
    const [internalValue, setInternalValue] = useState<T | T[] | undefined>(defaultValue);

    // 使用 ref 标记是否是受控模式（value 属性是否被传入过）
    const isControlledRef = useRef(value !== undefined);
    
    // 更新受控模式标记
    if (value !== undefined) {
        isControlledRef.current = true;
    }

    // 当前值（受控 / 非受控）
    // 受控模式下，即使 value 是 undefined 也使用 value
    // 非受控模式下，使用 internalValue
    const currentValue = isControlledRef.current ? value : internalValue;

    // 判断某个选项是否选中
    const isSelected = useCallback(
        (optionValue: T): boolean => {
            if (currentValue === undefined || currentValue === null) return false;
            if (Array.isArray(currentValue)) {
                return currentValue.includes(optionValue);
            }
            return currentValue === optionValue;
        },
        [currentValue]
    );

    // 处理选项点击
    const handlePress = useCallback(
        (optionValue: T) => {
            if (disabled) return;

            let newValue: T | T[];

            if (mode === 'single') {
                // 单选模式
                if (isSelected(optionValue) && deselectable) {
                    newValue = undefined as unknown as T;
                } else {
                    newValue = optionValue;
                }
            } else {
                // 多选模式
                const currentArr = Array.isArray(currentValue)
                    ? [...currentValue]
                    : currentValue !== undefined && currentValue !== null
                    ? [currentValue as T]
                    : [];

                if (currentArr.includes(optionValue)) {
                    // 已选中 -> 取消选中
                    if (min !== undefined && currentArr.length <= min) {
                        // 达到最小选择数，不允许取消
                        return;
                    }
                    newValue = currentArr.filter((v) => v !== optionValue);
                } else {
                    // 未选中 -> 选中
                    if (max !== undefined && currentArr.length >= max) {
                        // 达到最大选择数，不允许继续选择
                        return;
                    }
                    newValue = [...currentArr, optionValue];
                }
            }

            // 非受控模式：更新内部状态
            if (!isControlledRef.current) {
                setInternalValue(newValue);
            }

            onChange?.(newValue);
        },
        [mode, currentValue, value, disabled, deselectable, max, min, isSelected, onChange]
    );

    // 计算选项宽度（如果指定了列数）
    const itemWidth = useMemo(() => {
        if (!columns || columns <= 0) return undefined;
        // 扣除间距
        return `${(100 / columns).toFixed(2)}%` as unknown as number;
    }, [columns]);

    // 渲染单个选项
    const renderItem = (option: SelectionOption<T>, index: number) => {
        const selected = isSelected(option.value);
        const isDisabled = disabled || !!option.disabled;

        // 计算样式
        const itemDynamicStyle = {
            borderColor: selected ? activeColor : borderColor,
            backgroundColor: selected
                ? activeBackgroundColor
                : isDisabled
                ? disabledBackgroundColor
                : backgroundColor,
            borderRadius,
            borderWidth,
            paddingHorizontal: itemPadding?.horizontal ?? DEFAULTS.paddingHorizontal,
            paddingVertical: itemPadding?.vertical ?? DEFAULTS.paddingVertical,
            marginRight: index < options.length - 1 ? gap : 0,
            marginBottom: gap,
            ...(itemWidth ? { width: itemWidth } : {}),
        };

        const textDynamicStyle = {
            fontSize,
            color: isDisabled
                ? disabledTextColor
                : selected
                ? activeTextColor
                : textColor,
        };

        return (
            <TouchableOpacity
                key={String(option.value)}
                style={[
                    styles.item,
                    itemDynamicStyle,
                    itemStyle,
                    selected && activeItemStyle,
                    isDisabled && styles.itemDisabled,
                    isDisabled && disabledItemStyle,
                ]}
                onPress={() => handlePress(option.value)}
                disabled={isDisabled}
                activeOpacity={0.7}
            >
                {option.renderContent ? (
                    option.renderContent(selected)
                ) : (
                    <Text
                        style={[
                            styles.text,
                            textDynamicStyle,
                            textStyle,
                            selected && activeTextStyle,
                            isDisabled && styles.textDisabled,
                            isDisabled && disabledTextStyle,
                        ]}
                        numberOfLines={1}
                    >
                        {option.label}
                    </Text>
                )}
            </TouchableOpacity>
        );
    };

    return (
        <View style={[styles.container, { gap: 0 }, style]}>
            {options.map((option, index) => renderItem(option, index))}
        </View>
    );
}

// 使用泛型包装组件
export const DbSelectionButton = DbSelectionButtonInner as <T = string>(
    props: DbSelectionButtonProps<T>
) => React.ReactElement;

(DbSelectionButtonInner as any).displayName = 'DbSelectionButton';

export default DbSelectionButton;
export type {
    DbSelectionButtonProps,
    SelectionOption,
    SelectionMode,
    OptionStatus,
} from './DbSelectionButton.types';
